
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
  Search,
  Filter,
  SlidersHorizontal,
  Building2,
  ArrowRight,
  MapPin,
  DollarSign,
  Users,
  Calendar,
  Clock,
  Target,
  Award,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Grid3X3,
  List,
  Bookmark,
  Share2,
  Shield,
  Filter as FilterIcon
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';
import { useGetPublicProjectsQuery, useGetPublicStatisticsQuery } from '../store/slices/apiSlice';

const Projects = () => {
  const { token, user } = useSelector((state: RootState) => state.auth);

  // Set page metadata
  usePageMetadata('projects');
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch projects from API
  const { data: projectsResponse, isLoading: projectsLoading, error: projectsError } = useGetPublicProjectsQuery();
  const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
    Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

  // Fetch real statistics
  const { data: statsData } = useGetPublicStatisticsQuery();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Calculate average ROI
  const totalROI = projects.reduce((sum: number, project: any) => {
    const roi = parseFloat(project.expected_roi_percentage || '0');
    return sum + roi;
  }, 0);
  const averageROI = projects.length > 0 ? (totalROI / projects.length).toFixed(1) : '0';

  // Calculate funded projects count
  const projectsFunded = projects.filter((project: any) => {
    const isFunded = project.is_funded === true;
    const fundingGoal = parseFloat(project.funding_goal || project.original_funding_goal || '0');
    const amountRaised = parseFloat(project.amount_raised || project.original_amount_raised || '0');
    return isFunded || (fundingGoal > 0 && amountRaised >= fundingGoal);
  }).length;

  // Get total investors from API
  const totalInvestors = statsData?.data?.total_users || 0;

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };



  // Dynamic categories based on actual project data
  const categories = React.useMemo(() => {
    const categoryMap = new Map();
    projects.forEach(project => {
      const categoryName = project.category.name;
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + 1);
    });

    const categoryColors = {
      'Real Estate': 'from-green-500 to-emerald-600',
      'Technology': 'from-blue-500 to-cyan-600',
      'Solar project': 'from-yellow-500 to-orange-600',
      'Healthcare': 'from-red-500 to-pink-600',
      'default': 'from-blue-500 to-purple-600'
    };

    const dynamicCategories = Array.from(categoryMap.entries()).map(([name, count]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      count,
      color: categoryColors[name] || categoryColors.default
    }));

    return [
      { id: 'all', name: 'All Projects', count: projects.length, color: 'from-blue-500 to-purple-600' },
      ...dynamicCategories
    ];
  }, [projects]);

  // Dynamic statuses based on actual project data
  const statuses = React.useMemo(() => {
    const statusMap = new Map();
    projects.forEach(project => {
      const status = project.status === 'active' ? 'funding' : project.status;
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });

    const dynamicStatuses = Array.from(statusMap.entries()).map(([status, count]) => ({
      id: status,
      name: status === 'funding' ? 'Funding Open' : status.charAt(0).toUpperCase() + status.slice(1),
      count
    }));

    return [
      { id: 'all', name: 'All Status', count: projects.length },
      ...dynamicStatuses
    ];
  }, [projects]);

  // Dynamic locations based on actual project data
  const locations = React.useMemo(() => {
    const locationMap = new Map();
    projects.forEach(project => {
      const location = project.location.split(',')[0].trim(); // Get city name
      locationMap.set(location, (locationMap.get(location) || 0) + 1);
    });

    const dynamicLocations = Array.from(locationMap.entries()).map(([location, count]) => ({
      id: location.toLowerCase().replace(/\s+/g, '-'),
      name: location,
      count
    }));

    return [
      { id: 'all', name: 'All Locations', count: projects.length },
      ...dynamicLocations
    ];
  }, [projects]);

  // Helper functions for API data
  const formatCurrency = (amount: number, currencySymbol: string = '$') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount).replace('$', currencySymbol);
  };

  const getProgressPercentage = (raised: number, target: number) => {
    return Math.min((raised / target) * 100, 100);
  };

  const getDaysLeft = (deadline: string) => {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime()-today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Filtered projects based on search and filters
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.short_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.full_description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' ||
      project.category.name.toLowerCase().replace(/\s+/g, '-') === selectedCategory;

    const projectStatus = project.status === 'active' ? 'funding' : project.status;
    const matchesStatus = selectedStatus === 'all' || projectStatus === selectedStatus;

    const projectLocation = project.location.split(',')[0].trim().toLowerCase().replace(/\s+/g, '-');
    const matchesLocation = selectedLocation === 'all' || projectLocation === selectedLocation;

    return matchesSearch && matchesCategory && matchesStatus && matchesLocation;
  });

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'roi-high':
        return parseFloat(b.expected_roi_percentage)-parseFloat(a.expected_roi_percentage);
      case 'roi-low':
        return parseFloat(a.expected_roi_percentage)-parseFloat(b.expected_roi_percentage);
      case 'funding-progress':
        const progressA = (a.amount_raised || 0) / a.funding_goal;
        const progressB = (b.amount_raised || 0) / b.funding_goal;
        return progressB-progressA;
      case 'days-left':
        return getDaysLeft(a.deadline)-getDaysLeft(b.deadline);
      case 'funding-goal':
        return b.funding_goal-a.funding_goal;
      default:
        return new Date(b.created_at).getTime()-new Date(a.created_at).getTime(); // latest first
    }
  });

  // Pagination
  const projectsPerPage = 6;
  const totalPages = Math.ceil(sortedProjects.length / projectsPerPage);
  const currentProjects = sortedProjects.slice((currentPage-1) * projectsPerPage, currentPage * projectsPerPage);


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'funding':
        return 'bg-green-100 text-green-800';
      case 'funded':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} `}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left">
                <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
                  <Building2 className="w-4 h-4" />
                  Investment Opportunities
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Discover Premium Projects
                </h1>

                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Explore curated investment opportunities across real estate, technology, renewable energy,
                  and healthcare sectors. Start building your portfolio today.
                </p>

                {/* Search Bar */}
                <div className="mb-8">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search projects, categories, or locations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:bg-white transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{projects.length}</h3>
                        <p className="text-gray-600 text-sm">Active Projects</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{formatNumber(totalInvestors)}</h3>
                        <p className="text-gray-600 text-sm">Investors</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{averageROI}%</h3>
                        <p className="text-gray-600 text-sm">Avg. ROI</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side-Call to Action */}
              <div className="relative">
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 text-center">
                  <div className="mb-6">
                    <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Invest?</h3>
                    <p className="text-gray-600">Start your investment journey with our curated opportunities</p>
                  </div>

                  <div className="space-y-4">
                    {!token ? (
                      <a
                        href="https://app.diasporawise.com"
                        className="block w-full bg-gray-800 text-white py-4 rounded-xl font-semibold hover:bg-gray-900 transition-all duration-200 text-center lg:hidden"
                      >
                        Get Started Today
                      </a>
                    ) : (
                      <Link
                        to="/dashboard"
                        className="block w-full bg-gray-800 text-white py-4 rounded-xl font-semibold hover:bg-gray-900 transition-all duration-200"
                      >
                        Go to Dashboard
                      </Link>
                    )}
                    <Link
                      to="/about"
                      className="block w-full bg-white border border-gray-300 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
                    >
                      Learn More
                    </Link>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-gray-600 text-sm mb-3">Trusted by investors worldwide</p>
                    <div className="flex items-center justify-center gap-4 text-gray-500">
                      <Shield className="w-5 h-5" />
                      <span className="text-sm">Bank-grade Security</span>
                      <span className="text-gray-300">•</span>
                      <Award className="w-5 h-5" />
                      <span className="text-sm">Verified Projects</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Categories */}
      <section className="py-12 px-6 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${selectedCategory === category.id
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    } `}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-4">
              {/* Sort Options */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="latest">Latest</option>
                <option value="roi-high">Highest ROI</option>
                <option value="roi-low">Lowest ROI</option>
                <option value="funding-progress">Funding Progress</option>
                <option value="days-left">Days Left</option>
                <option value="investors">Most Investors</option>
              </select>

              {/* View Mode */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors duration-300 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
                    } `}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors duration-300 ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
                    } `}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors duration-300"
              >
                <FilterIcon className="w-4 h-4" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Project Status</label>
                  <div className="space-y-2">
                    {statuses.map((status) => (
                      <button
                        key={status.id}
                        onClick={() => setSelectedStatus(status.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-300 ${selectedStatus === status.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'hover:bg-gray-50'
                          } `}
                      >
                        {status.name} ({status.count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Location</label>
                  <div className="space-y-2">
                    {locations.map((location) => (
                      <button
                        key={location.id}
                        onClick={() => setSelectedLocation(location.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-300 ${selectedLocation === location.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'hover:bg-gray-50'
                          } `}
                      >
                        {location.name} ({location.count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Risk Level Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Risk Level</label>
                  <div className="space-y-2">
                    {['All', 'Low', 'Medium', 'High'].map((risk) => (
                      <button
                        key={risk}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors duration-300"
                      >
                        {risk} Risk
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              All Investment Opportunities
            </h2>
            <p className="text-gray-600">
              Showing {currentProjects.length} of {filteredProjects.length} projects
            </p>
          </div>

          {projectsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading projects...</p>
              </div>
            </div>
          ) : projectsError ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Projects</h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          ) : currentProjects.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-6'}>
              {currentProjects.map((project) => (
                <div key={project.project_id} className="group cursor-pointer">
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                    {/* Project Image */}
                    <div className="relative overflow-hidden">
                      <img
                        src={project.images && project.images.length > 0 ? project.images[0].image_url : 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80'}
                        alt={project.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status === 'active' ? 'funding' : project.status)} `}>
                          {project.status === 'active' ? 'Funding Open' :
                            project.status === 'funded' ? 'Fully Funded' : 'Completed'}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4 flex gap-2">
                        <button className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300">
                          <Bookmark className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300">
                          <Share2 className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      {/* Category & Location */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-semibold">
                          {project.category.name}
                        </span>
                        <span className="flex items-center gap-1 text-gray-500 text-sm">
                          <MapPin className="w-3 h-3" />
                          {project.location.split(',')[0].trim()}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                        {project.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {project.short_description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                          {project.category.name}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                          {project.location.split(',')[0].trim()}
                        </span>
                      </div>

                      {/* Funding Progress */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">Funding Progress</span>
                          <span className="text-sm text-gray-600">
                            {getProgressPercentage(project.amount_raised || 0, project.funding_goal).toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${getProgressPercentage(project.amount_raised || 0, project.funding_goal)}% ` }}
                          ></div>
                        </div>
                      </div>

                      {/* Project Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center">
                          <p className="text-lg font-bold text-gray-900">{formatCurrency(project.funding_goal, project.currency_symbol)}</p>
                          <p className="text-xs text-gray-500">Target Amount</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-600">{project.expected_roi_percentage}%</p>
                          <p className="text-xs text-gray-500">Expected ROI</p>
                        </div>
                      </div>

                      {/* Bottom Stats */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {project.roi_duration_months} months
                          </span>
                          {getDaysLeft(project.deadline) > 0 && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {getDaysLeft(project.deadline)} days left
                            </span>
                          )}
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-600">
                          {project.status === 'active' ? 'Active' : 'Closed'}
                        </span>
                      </div>

                      {/* Invest Button */}
                      <Link
                        to={`/ projects / ${project.project_id} `}
                        state={{ project }}
                        className="mt-4 w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center gap-2"
                      >
                        <DollarSign className="w-4 h-4" />
                        Invest Now
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
              <div className="text-gray-400 mb-4">
                <SlidersHorizontal className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-600">Try adjusting your filters or search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage-1))}
                disabled={currentPage === 1}
                className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronDown className="w-5 h-5 rotate-90" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-colors duration-300 ${currentPage === page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    } `}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronDown className="w-5 h-5 -rotate-90" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Simple CTA Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full text-sm font-medium text-gray-600 mb-6 border border-gray-200">
            <Sparkles className="w-4 h-4" />
            Start Investing Today
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Build Your Portfolio?
          </h2>

          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of investors who are already building wealth through our curated investment opportunities.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!token ? (
              <a
                href="https://app.diasporawise.com"
                className="px-8 py-4 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition-colors duration-200 text-center lg:hidden"
              >
                Get Started
              </a>
            ) : (
              <Link
                to="/dashboard"
                className="px-8 py-4 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition-colors duration-200"
              >
                Go to Dashboard
              </Link>
            )}
            <Link
              to="/about"
              className="px-8 py-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Project Investment Questions"
        subtitle="Everything you need to know about investing in our curated projects"
        faqs={[
          {
            id: 1,
            question: "How do I choose the right project to invest in?",
            answer: "Each project on our platform includes detailed information about the investment opportunity, risk assessment, expected returns, and project timeline. We recommend reviewing the project details, financial projections, and risk factors before making your investment decision. Our team is also available to provide guidance and answer any specific questions you may have."
          },
          {
            id: 2,
            question: "What information is provided for each project?",
            answer: "Every project includes comprehensive details such as project description, location, investment amount, expected returns, timeline, risk assessment, developer information, financial projections, and legal documentation. We ensure full transparency so you can make informed investment decisions."
          },
          {
            id: 3,
            question: "How are project risks assessed and communicated?",
            answer: "Our expert team conducts thorough risk assessments for each project, evaluating factors such as market conditions, regulatory compliance, developer track record, and project feasibility. Risk levels are clearly communicated through our rating system, and detailed risk factors are provided in each project's documentation."
          },
          {
            id: 4,
            question: "What happens after I invest in a project?",
            answer: "After your investment, you'll receive confirmation and access to your investor dashboard where you can track project progress, view updates, and monitor your investment performance. You'll receive regular updates from the project team and can access detailed reports on project milestones and financial performance."
          },
          {
            id: 5,
            question: "Can I invest in multiple projects simultaneously?",
            answer: "Yes, you can diversify your portfolio by investing in multiple projects. Our platform allows you to spread your investments across different sectors, risk levels, and timelines to create a balanced investment portfolio that aligns with your financial goals and risk tolerance."
          },
          {
            id: 6,
            question: "What is the typical investment timeline for projects?",
            answer: "Investment timelines vary by project type. Real estate projects typically range from 12-36 months, while technology startups may have longer timelines of 3-7 years. Each project clearly states its expected timeline, and you can filter projects by duration to find opportunities that match your investment horizon."
          }
        ]}
      />

      <Footer />
    </div>
  );
};

export default Projects;

